/**
 * PURPOSE: Pad de firma de una casilla de la planilla FEDUP (FTR-0062), SOBRE el visor (no sobre la planilla) y fuera de los
 *   contenedores con zoom (Modal propio): el trazo cae donde está el cursor.
 * RESPONSIBILITIES:
 *  - Sí: lienzo grande (mouse, dedo, lápiz) con etiqueta fija de la casilla ("Capitán · UCSUR", "Árbitro 1"), sin pedir ningún dato;
 *    Borrar, Cancelar y Guardar (deshabilitado con el lienzo vacío).
 *  - Sí: casilla firmada → muestra la firma actual con "Rehacer" (nueva firma que reemplaza) y "Quitar" (con confirmación).
 *  - Sí: Esc cierra SOLO el pad; un clic fuera no lo cierra si ya hay trazos; con el partido oficializado (423 desde otra pestaña)
 *    pasa a candado con "Partido oficializado: las firmas ya no se pueden modificar".
 *  - No: no habla con el backend (onSave / onRemove del controlador) ni pide geolocalización ni muestra diálogos del navegador.
 * COLLABORATORS:
 *  - SignatureCanvas.jsx, useFedupSignatureController.js, FedupPreviewModal.jsx
 * NOTES:
 *  - z-index 1800 (el visor está a 1700); en pantalla completa del navegador se monta en el mismo contenedor que el visor.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/FedupSignaturePad.jsx
 */

import React, { useEffect, useRef, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import { FiAlertTriangle, FiLock, FiRotateCcw, FiTrash2 } from "react-icons/fi";

import SignatureCanvas from "./SignatureCanvas.jsx";
import { OFFICIAL_LOCK_MESSAGE, OFFICIAL_SIGN_NOTE } from "./fedupSignatures.utils.js";

const PAD_TITLE_ID = "fedup-signature-pad-title";

const BTN = "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-4 text-xs font-bold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40";
const BTN_GHOST = `${BTN} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
const BTN_PRIMARY = `${BTN} bg-slate-900 text-white hover:bg-slate-700`;
const BTN_DANGER = `${BTN} border border-rose-300 bg-white text-rose-700 hover:bg-rose-50`;

export default function FedupSignaturePad({
  slot,
  modeLabel = "",
  container = null,
  busy = false,
  error = null,
  locked = false,
  official = false,
  imageUrl = null,
  ensureImage,
  onClose,
  onSave,
  onRemove,
}) {
  const open = Boolean(slot);
  const canvasRef = useRef(null);
  const [view, setView] = useState("draw");
  const [strokes, setStrokes] = useState(0);
  const slotId = slot?.slot || null;
  const signatureId = slot?.signature?.id ?? null;

  useEffect(() => {
    if (!slotId) return;
    setView(signatureId ? "current" : "draw");
    setStrokes(0);
    if (signatureId) ensureImage?.(slotId);
  }, [slotId, signatureId, ensureImage]);

  const hasStrokes = strokes > 0;

  const handleClose = (_event, reason) => {
    if (busy) return;
    if (reason === "backdropClick" && view === "draw" && hasStrokes && !locked) return;
    onClose?.();
  };

  const handleSave = () => {
    if (!slot || busy) return;
    const png = canvasRef.current?.toSignaturePng({ inkBoxPt: slot.inkBox || null });
    if (png) onSave?.(slot.slot, png);
  };

  const clear = () => {
    canvasRef.current?.clear();
    setStrokes(0);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      container={container || undefined}
      sx={{ zIndex: 1800 }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <ModalDialog
        role="dialog"
        aria-modal="true"
        aria-labelledby={PAD_TITLE_ID}
        data-fedup-signature-pad="true"
        data-volleyball-shortcuts-ignore="true"
        sx={{ p: 0, gap: 0, overflow: "hidden", width: "min(94vw, 980px)", maxWidth: "94vw", borderRadius: "14px" }}
      >
        {slot ? (
          <>
            <div className="flex items-baseline justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                <h2 id={PAD_TITLE_ID} className="text-sm font-extrabold text-slate-900">
                  {slot.label}
                </h2>
                {modeLabel ? <span className="text-[11px] text-slate-400">{modeLabel}</span> : null}
              </div>
            </div>

            <div className="px-4 py-3">
              {locked ? (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm font-semibold text-slate-700" role="status">
                  <FiLock className="shrink-0 text-lg text-slate-500" aria-hidden="true" />
                  {OFFICIAL_LOCK_MESSAGE}
                </div>
              ) : view === "current" || view === "confirmRemove" ? (
                <div
                  className="flex w-full items-center justify-center overflow-hidden rounded-xl border border-slate-300 bg-white"
                  style={{ aspectRatio: "3.5 / 1" }}
                  data-fedup-signature-current="true"
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt={`Firma actual de ${slot.label}`} className="max-h-[85%] max-w-[85%] object-contain" draggable={false} />
                  ) : (
                    <span className="text-xs text-slate-400">Cargando la firma…</span>
                  )}
                </div>
              ) : (
                <>
                {official && !signatureId ? (
                  <p role="note" data-fedup-signature-official-note="true" className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
                    <FiLock className="shrink-0" aria-hidden="true" />
                    {OFFICIAL_SIGN_NOTE}
                  </p>
                ) : null}
                <div className="relative w-full overflow-hidden rounded-xl border border-slate-300 bg-white" style={{ aspectRatio: "3.5 / 1" }}>
                  {/* línea de base y ayuda (no reciben el puntero) */}
                  <div aria-hidden="true" className="pointer-events-none absolute inset-x-[6%] bottom-[24%] border-b border-dashed border-slate-300" />
                  {!hasStrokes ? (
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold text-slate-300">
                      Firme aquí
                    </div>
                  ) : null}
                  <div className="absolute inset-0">
                    <SignatureCanvas ref={canvasRef} disabled={busy} onStrokesChange={setStrokes} ariaLabel={`Lienzo de firma: ${slot.label}`} />
                  </div>
                </div>
                </>
              )}

              {error && !locked ? (
                <p role="alert" className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-rose-700">
                  <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
                  {error.message}
                </p>
              ) : null}
              {view === "confirmRemove" && !locked ? (
                <p className="mt-2 text-xs font-semibold text-slate-700">¿Quitar la firma de {slot.label}? La casilla quedará vacía en la planilla.</p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
              {locked ? (
                <button type="button" className={BTN_PRIMARY} onClick={() => onClose?.()}>
                  Cerrar
                </button>
              ) : view === "current" ? (
                <>
                  <button type="button" className={BTN_DANGER} onClick={() => setView("confirmRemove")} disabled={busy}>
                    <FiTrash2 aria-hidden="true" /> Quitar
                  </button>
                  <button type="button" className={BTN_GHOST} onClick={() => onClose?.()} disabled={busy}>
                    Cerrar
                  </button>
                  <button type="button" className={BTN_PRIMARY} onClick={() => setView("draw")} disabled={busy}>
                    <FiRotateCcw aria-hidden="true" /> Rehacer
                  </button>
                </>
              ) : view === "confirmRemove" ? (
                <>
                  <button type="button" className={BTN_GHOST} onClick={() => setView("current")} disabled={busy}>
                    No, conservarla
                  </button>
                  <button type="button" className={`${BTN} bg-rose-600 text-white hover:bg-rose-500`} onClick={() => onRemove?.(slot.slot)} disabled={busy}>
                    {busy ? "Quitando…" : "Quitar firma"}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className={BTN_GHOST} onClick={clear} disabled={busy || !hasStrokes}>
                    Borrar
                  </button>
                  <button type="button" className={BTN_GHOST} onClick={() => onClose?.()} disabled={busy}>
                    Cancelar
                  </button>
                  <button type="button" className={BTN_PRIMARY} onClick={handleSave} disabled={busy || !hasStrokes}>
                    {busy ? "Guardando…" : "Guardar"}
                  </button>
                </>
              )}
            </div>
          </>
        ) : null}
      </ModalDialog>
    </Modal>
  );
}
